import { Notification } from '@citruscamps/citrus-client'
import { FloatingTree } from '@floating-ui/react'
import { faSpinner, faCircle as farCircle } from '@fortawesome/pro-regular-svg-icons'
import {
  faBell,
  faEllipsisV,
  faRedo,
  faTrashAlt,
  faTrashUndo,
  faCircle as fasCircle,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import React from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { Link } from 'react-router-dom'
import { Drawer } from '../../../../../components/Drawer/Drawer'
import { ErrorContent } from '../../../../../components/ErrorContent/ErrorContent'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { LoadingRows } from '../../../../../components/LoadingRows/LoadingRows'
import { Popover } from '../../../../../components/Popover/Popover'
import { usePopper } from '../../../../../components/Popover/hooks/usePopper'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { TruncatedText } from '../../../../../components/TruncatedText/TruncatedText'
import { ROUTES } from '../../../../../constants/routes'
import { UndrawMultitasking } from '../../../../../images/undraw'
import {
  IFetchNotificationsProps,
  NotificationFetchProps,
  ProvideNotifications,
  useFetchNotifications,
  useNotifications,
} from '../hooks/useFetchNotifications'

interface IProps {
  programId: string
  className?: string
}

export const NotificationsButton = ({ programId, ...props }: IProps) => {
  const { target, toggle, targetProps, ...drawerProps } = usePopper<HTMLButtonElement>()
  const fetchNotificationProps: IFetchNotificationsProps = {
    programId,
    fetchProps: {
      sort: 'created',
      order: 'DESC',
      filter: { is_archived: false, topic_group: ['program', 'product'] },
    },
  }
  const {
    data: notifications,
    isLoading,
    hasUnread,
    setFetchProps,
  } = useFetchNotifications(fetchNotificationProps)
  return (
    <ProvideNotifications {...fetchNotificationProps}>
      <button
        ref={target}
        {...targetProps}
        className={`btn btn-link text-body ${props.className || ''}`.trim()}
        disabled={!notifications || undefined}
      >
        {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        {!isLoading && (
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon icon={faBell} flip="horizontal" transform="up-2" />
            {hasUnread && (
              <FontAwesomeIcon
                icon={fasCircle}
                className="text-danger"
                transform="shrink-8 right-8 down-8"
              />
            )}
          </span>
        )}
      </button>
      <FloatingTree>
        <Drawer
          toggle={toggle}
          {...drawerProps}
          actionButton={<NotificationActionButton />}
          className="bg-body"
        >
          <NotificationDrawer
            onChangeFetchProps={(props) => {
              setFetchProps(props)
            }}
          />
        </Drawer>
      </FloatingTree>
    </ProvideNotifications>
  )
}

const NotificationActionButton = (props: React.HTMLAttributes<HTMLButtonElement>) => {
  const { handleMarkAllAsRead, handleArchiveAll } = useNotifications()
  const { target, toggle, targetProps, ...tooltipProps } = usePopper<HTMLButtonElement>({
    placement: 'left-start',
  })

  return (
    <>
      <button
        id="btn-notifications-action"
        {...props}
        {...targetProps}
        className={props.className || 'btn btn-link text-body'}
        ref={target}
      >
        <FontAwesomeIcon icon={faEllipsisV} />
      </button>
      <Popover style={{ zIndex: 1090 }} {...tooltipProps} toggle={toggle}>
        <button
          type="button"
          className="dropdown-item"
          onClick={() => {
            handleMarkAllAsRead()
            toggle()
          }}
        >
          Mark all as read
        </button>
        <button
          type="button"
          className="dropdown-item border-top"
          onClick={() => {
            handleArchiveAll()
            toggle()
          }}
        >
          Archive all
        </button>
        <Link className="dropdown-item border-top" to={ROUTES.NOTIFICATION_SETTINGS}>
          Settings
        </Link>
      </Popover>
    </>
  )
}

interface INotificationDrawerProps {
  onChangeFetchProps: (props: NotificationFetchProps) => void
}

export const NotificationDrawer = ({
  onChangeFetchProps: handleChangeFetchProps,
}: INotificationDrawerProps) => {
  const {
    data: notifications,
    error,
    hasNextPage,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleArchive,
    handleUnarchive,
    setFetchProps,
  } = useNotifications()
  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
  })

  if (isLoading && notifications?.length === 0) {
    return (
      <>
        <h4 className="m-0 pt-2 pb-3">Notifications</h4>
        <NotificationNavTabs onClick={handleChangeFetchProps} />
        <LoadingRows />
        <LoadingRows />
        <LoadingRows />
      </>
    )
  }

  if (isError || !notifications) {
    return (
      <>
        <h4 className="m-0 pt-2 pb-3">Notifications</h4>
        <NotificationNavTabs onClick={handleChangeFetchProps} />
        <ErrorContent size="xs" className="text-body-secondary" error={error}>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setFetchProps()
            }}
          >
            <FontAwesomeIcon className="me-2" icon={faRedo} />
            Refresh list
          </button>
        </ErrorContent>
      </>
    )
  }
  if (notifications.length === 0) {
    return (
      <>
        <h4 className="m-0 pt-2 pb-3">Notifications</h4>
        <NotificationNavTabs onClick={handleChangeFetchProps} />
        <ErrorContent
          size="lg"
          className="my-5"
          image={UndrawMultitasking}
          description="No new notifications, for now. Go ahead and take a break."
          header="Nicely done!"
          hideButton={true}
        />
      </>
    )
  }
  return (
    <>
      <h4 className="m-0 pt-2 pb-3">Notifications</h4>
      <NotificationNavTabs onClick={handleChangeFetchProps} />
      {notifications.map((notification) => (
        <NotificationDrawerItem
          className="px-3"
          key={notification.id}
          notification={notification}
          onMarkAsRead={handleMarkAsRead}
          onMarkAsUnread={handleMarkAsUnread}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
        />
      ))}
      {isFetching && (
        <>
          <LoadingRows />
          <LoadingRows />
          <LoadingRows />
        </>
      )}
      <div ref={infiniteRef}>&nbsp;</div>
    </>
  )
}

interface INotificationNavTabsProps {
  onClick: (fetchProps: NotificationFetchProps) => void
}

const NotificationNavTabs = ({ onClick: handleClick }: INotificationNavTabsProps) => {
  const { fetchProps } = useNotifications()
  const options = [
    {
      id: 'all',
      label: 'All',
      value: {
        is_read: undefined,
        is_archived: false,
      },
    },
    {
      id: 'unread',
      label: 'Unread',
      value: {
        is_read: false,
        is_archived: false,
      },
    },
    {
      id: 'archived',
      label: 'Archived',
      value: {
        is_read: undefined,
        is_archived: true,
      },
    },
  ]
  let id: string
  if (fetchProps?.filter.is_archived === true) {
    id = 'archived'
  } else if (fetchProps?.filter.is_read === false) {
    id = 'unread'
  } else {
    id = 'all'
  }
  return (
    <div className="nav nav-underline nav-overflow justify-content-between mb-4">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`nav-item nav-link ${option.id === id ? 'active' : ''}`}
          onClick={() => handleClick({ ...fetchProps, filter: option.value })}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

interface INotificationDrawerItemProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  notification: Notification
  onMarkAsRead: (id: string) => Promise<void>
  onMarkAsUnread: (id: string) => Promise<void>
  onArchive: (id: string) => Promise<void>
  onUnarchive: (id: string) => Promise<void>
}

const NotificationDrawerItem = (allProps: INotificationDrawerItemProps) => {
  const {
    notification,
    onMarkAsRead: handleMarkAsRead,
    onMarkAsUnread: handleMarkAsUnread,
    onArchive: handleArchive,
    onUnarchive: handleUnarchive,
    ...props
  } = allProps
  return (
    <div {...props}>
      <div className="d-flex justify-content-between" style={{ minHeight: 72 }}>
        <div>
          <button
            type="button"
            className="btn btn-link text-body text-decoration-none p-0 text-start"
            style={{ boxShadow: 'none' }}
            onClick={async () => {
              await handleMarkAsRead(notification.id)
              window.location.href = notification.url
            }}
          >
            <TruncatedText lines={2} className="h6 m-0 p-0">
              {notification.subject}
            </TruncatedText>
            <div className="pb-2">
              <span className="text-body-secondary small">
                {DateTime.fromJSDate(notification.created).toLocaleString(DateTime.DATE_FULL)}
              </span>
            </div>
          </button>
        </div>
        <div>
          {notification.is_read && (
            <Tooltip
              title="Mark as unread"
              placement="left"
              popupProps={{ style: { zIndex: 1090 } }}
            >
              <LoadingButton
                type="button"
                className="btn btn-link btn-sm text-body-secondary"
                onClick={async () => await handleMarkAsUnread(notification.id)}
              >
                <FontAwesomeIcon icon={farCircle} fixedWidth={true} />
              </LoadingButton>
            </Tooltip>
          )}
          {!notification.is_read && (
            <Tooltip title="Mark as read" placement="left" popupProps={{ style: { zIndex: 1090 } }}>
              <LoadingButton
                type="button"
                className="btn btn-link btn-sm text-danger"
                onClick={async () => await handleMarkAsRead(notification.id)}
              >
                <FontAwesomeIcon icon={fasCircle} transform="shrink-6" fixedWidth={true} />
              </LoadingButton>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="d-flex justify-content-end" style={{ marginTop: '-2rem' }}>
        {!notification.is_archived ? (
          <Tooltip title="Archive" placement="left" popupProps={{ style: { zIndex: 1090 } }}>
            <LoadingButton
              type="button"
              className="btn btn-link btn-sm"
              onClick={async () => await handleArchive(notification.id)}
            >
              <FontAwesomeIcon icon={faTrashAlt} fixedWidth={true} />
            </LoadingButton>
          </Tooltip>
        ) : (
          <Tooltip title="Unarchive" placement="left" popupProps={{ style: { zIndex: 1090 } }}>
            <LoadingButton
              type="button"
              className="btn btn-link btn-sm"
              onClick={async () => await handleUnarchive(notification.id)}
            >
              <FontAwesomeIcon icon={faTrashUndo} fixedWidth={true} />
            </LoadingButton>
          </Tooltip>
        )}
      </div>
      <hr />
    </div>
  )
}
